/**
 * OknoRozmowy - prawa kolumna panelu: historia wiadomosci + pole pisania.
 *
 * Odpowiada za:
 *  - pobranie ostatniej strony wiadomosci i doladowywanie historii W GORE
 *    (backend paginuje wstecz przez ?before_id=),
 *  - grupowanie kolejnych wiadomosci tego samego autora i separatory dni,
 *  - autoscroll na dol, ale BEZ wyrywania widoku, gdy uzytkownik czyta historie
 *    wyzej (wtedy pojawia sie przycisk "Nowe wiadomości"),
 *  - wysylke tekstu, emoji i zalacznikow (spinacz + aparat) z paskiem postepu,
 *  - oznaczanie przeczytania (POST /czytaj, wskaznik idzie tylko w gore).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import EmojiPicker from './EmojiPicker';
import Wiadomosc, { etykietaDnia, formatujRozmiar, parsujDate } from './Wiadomosc';
import {
  pobierzWiadomosci,
  wyslijWiadomosc,
  wyslijZalacznik,
  oznaczPrzeczytane,
} from '../../services/komunikatorService';

const LIMIT_STRONY = 50;
const MAX_ROZMIAR = 15 * 1024 * 1024; // zgodnie z limitem komunikator_storage
const PROG_DOLU = 90;                 // px od dolu = "uzytkownik patrzy na koniec"
const PRZERWA_GRUPY = 5 * 60 * 1000;  // 5 minut - dluzsza przerwa = nowy naglowek

/** Scalenie dwoch list wiadomosci po id (rosnaco). Nowsza wersja rekordu wygrywa. */
const scal = (a, b) => {
  const mapa = new Map();
  (a || []).forEach((w) => mapa.set(w.id, w));
  (b || []).forEach((w) => mapa.set(w.id, w));
  return Array.from(mapa.values()).sort((x, y) => x.id - y.id);
};

const tenSamDzien = (a, b) => {
  const da = parsujDate(a);
  const db = parsujDate(b);
  if (!da || !db) return false;
  return da.getFullYear() === db.getFullYear()
    && da.getMonth() === db.getMonth()
    && da.getDate() === db.getDate();
};

const OknoRozmowy = ({
  rozmowa,
  sygnal = 0,
  waski = false,
  onWstecz,
  onPrzeczytane,
  onZmianaListy,
  onPodglad,
}) => {
  const [wiadomosci, setWiadomosci] = useState([]);
  const [ladowanie, setLadowanie] = useState(false);
  const [ladowanieStarszych, setLadowanieStarszych] = useState(false);
  const [maWiecej, setMaWiecej] = useState(false);
  const [blad, setBlad] = useState(null);

  const [tekst, setTekst] = useState('');
  const [wysylanie, setWysylanie] = useState(false);
  const [plik, setPlik] = useState(null);
  const [podgladPliku, setPodgladPliku] = useState(null);
  const [postep, setPostep] = useState(0);
  const [pickerOtwarty, setPickerOtwarty] = useState(false);
  const [nowePonizej, setNowePonizej] = useState(false);

  const listaRef = useRef(null);
  const polRef = useRef(null);
  const wejscePlikRef = useRef(null);
  const wejsceZdjecieRef = useRef(null);
  const przyDoleRef = useRef(true);
  const ostatniIdRef = useRef(null);
  const oznaczoneRef = useRef(0);
  const zywyRef = useRef(true);

  const rozmowaId = rozmowa ? rozmowa.id : null;

  useEffect(() => {
    zywyRef.current = true;
    return () => { zywyRef.current = false; };
  }, []);

  const doDolu = useCallback((zachowanie = 'auto') => {
    const el = listaRef.current;
    if (!el) return;
    // requestAnimationFrame: DOM musi sie najpierw przemalowac,
    // inaczej scrollHeight jest jeszcze sprzed dolozenia wiadomosci
    requestAnimationFrame(() => {
      const kontener = listaRef.current;
      if (!kontener) return;
      // scrollTo z opcjami nie jest dostepne wszedzie (starsze Safari, jsdom) -
      // zwykly scrollTop jest bezpiecznym zapasem.
      if (typeof kontener.scrollTo === 'function') {
        kontener.scrollTo({ top: kontener.scrollHeight, behavior: zachowanie });
      } else {
        kontener.scrollTop = kontener.scrollHeight;
      }
      przyDoleRef.current = true;
      setNowePonizej(false);
    });
  }, []);

  // --- pobranie ostatniej strony (wejscie w rozmowe oraz odswiezenie z pollingu)
  const odswiez = useCallback(async (pierwszeWejscie) => {
    if (!rozmowaId) return;
    if (pierwszeWejscie) setLadowanie(true);
    try {
      const d = await pobierzWiadomosci(rozmowaId, { limit: LIMIT_STRONY });
      if (!zywyRef.current) return;
      const rosnaco = [...d.wiadomosci].reverse();
      setBlad(null);
      if (pierwszeWejscie) {
        setWiadomosci(rosnaco);
        setMaWiecej(!!d.ma_wiecej);
      } else {
        setWiadomosci((prev) => scal(prev, rosnaco));
      }
    } catch (e) {
      if (!zywyRef.current) return;
      // Panel ma NIE znikac przy bledzie sieci - pokazujemy pasek z ponowieniem.
      setBlad(e.message || 'Nie udało się pobrać wiadomości');
    } finally {
      if (zywyRef.current && pierwszeWejscie) setLadowanie(false);
    }
  }, [rozmowaId]);

  // wejscie w rozmowe: pelny reset stanu
  useEffect(() => {
    setWiadomosci([]);
    setMaWiecej(false);
    setBlad(null);
    setNowePonizej(false);
    setPickerOtwarty(false);
    przyDoleRef.current = true;
    ostatniIdRef.current = null;
    oznaczoneRef.current = 0;
    if (rozmowaId) odswiez(true);
  }, [rozmowaId, odswiez]);

  // sygnal z widgetu (polling wykryl nowe wiadomosci) - dociagamy ostatnia strone
  useEffect(() => {
    if (!rozmowaId || !sygnal) return;
    odswiez(false);
  }, [sygnal, rozmowaId, odswiez]);

  // --- autoscroll / przycisk "Nowe wiadomości"
  useEffect(() => {
    if (!wiadomosci.length) return;
    const ostatnia = wiadomosci[wiadomosci.length - 1];
    if (ostatnia.id === ostatniIdRef.current) return; // doladowanie historii w gore
    const pierwszyRender = ostatniIdRef.current === null;
    ostatniIdRef.current = ostatnia.id;
    if (pierwszyRender || przyDoleRef.current || ostatnia.wlasna) {
      doDolu(pierwszyRender ? 'auto' : 'smooth');
    } else {
      setNowePonizej(true);
    }
  }, [wiadomosci, doDolu]);

  // --- oznaczenie przeczytania (tylko w gore, jeden POST na nowe maksimum)
  useEffect(() => {
    if (!rozmowaId || !wiadomosci.length) return;
    const maxId = wiadomosci[wiadomosci.length - 1].id;
    if (!maxId || maxId <= oznaczoneRef.current) return;
    oznaczoneRef.current = maxId;
    oznaczPrzeczytane(rozmowaId, maxId)
      .then(() => { if (onPrzeczytane) onPrzeczytane(rozmowaId, maxId); })
      .catch(() => { oznaczoneRef.current = 0; }); // pozwol sprobowac ponownie
  }, [wiadomosci, rozmowaId, onPrzeczytane]);

  // --- doladowanie historii w gore
  const dociagnijStarsze = useCallback(async () => {
    if (!rozmowaId || !maWiecej || ladowanieStarszych || !wiadomosci.length) return;
    setLadowanieStarszych(true);
    const el = listaRef.current;
    const wysokoscPrzed = el ? el.scrollHeight : 0;
    try {
      const d = await pobierzWiadomosci(rozmowaId, {
        beforeId: wiadomosci[0].id,
        limit: LIMIT_STRONY,
      });
      if (!zywyRef.current) return;
      const rosnaco = [...d.wiadomosci].reverse();
      setMaWiecej(!!d.ma_wiecej);
      setWiadomosci((prev) => scal(rosnaco, prev));
      // przywrocenie pozycji - inaczej widok skoczylby na sam gorny brzeg
      requestAnimationFrame(() => {
        if (listaRef.current) {
          listaRef.current.scrollTop = listaRef.current.scrollHeight - wysokoscPrzed;
        }
      });
    } catch (e) {
      if (zywyRef.current) toast.error(e.message || 'Nie udało się pobrać starszych wiadomości');
    } finally {
      if (zywyRef.current) setLadowanieStarszych(false);
    }
  }, [rozmowaId, maWiecej, ladowanieStarszych, wiadomosci]);

  const naScroll = useCallback(() => {
    const el = listaRef.current;
    if (!el) return;
    przyDoleRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < PROG_DOLU;
    if (przyDoleRef.current && nowePonizej) setNowePonizej(false);
    if (el.scrollTop < 48) dociagnijStarsze();
  }, [dociagnijStarsze, nowePonizej]);

  // --- podglad wybranego pliku przed wyslaniem
  useEffect(() => {
    if (!plik || !plik.type || !plik.type.startsWith('image/')) {
      setPodgladPliku(null);
      return undefined;
    }
    let url = null;
    try {
      url = URL.createObjectURL(plik);
    } catch (e) {
      url = null; // brak podgladu nie moze wywalic okna rozmowy
    }
    setPodgladPliku(url);
    return () => {
      if (!url) return;
      try { URL.revokeObjectURL(url); } catch (e) { /* nieistotne */ }
    };
  }, [plik]);

  const wybierzPlik = (ev) => {
    const f = ev.target.files && ev.target.files[0];
    ev.target.value = ''; // pozwala wybrac ten sam plik ponownie
    if (!f) return;
    if (f.size > MAX_ROZMIAR) {
      toast.error(`Plik jest za duży (${formatujRozmiar(f.size)}). Maksymalnie 15 MB.`);
      return;
    }
    setPlik(f);
    if (polRef.current) polRef.current.focus();
  };

  const wstawEmoji = (emoji) => {
    const ta = polRef.current;
    if (!ta) {
      setTekst((t) => t + emoji);
      return;
    }
    const start = typeof ta.selectionStart === 'number' ? ta.selectionStart : tekst.length;
    const koniec = typeof ta.selectionEnd === 'number' ? ta.selectionEnd : tekst.length;
    const nowy = tekst.slice(0, start) + emoji + tekst.slice(koniec);
    setTekst(nowy);
    requestAnimationFrame(() => {
      if (!polRef.current) return;
      polRef.current.focus();
      const pozycja = start + emoji.length;
      polRef.current.setSelectionRange(pozycja, pozycja);
    });
  };

  const wyslij = async () => {
    if (wysylanie || !rozmowaId) return;
    const tresc = tekst.trim();
    if (!tresc && !plik) return;

    setWysylanie(true);
    setPostep(0);
    try {
      let nowa;
      if (plik) {
        nowa = await wyslijZalacznik(rozmowaId, plik, tresc, setPostep);
      } else {
        nowa = await wyslijWiadomosc(rozmowaId, tresc);
      }
      if (!zywyRef.current) return;
      setTekst('');
      setPlik(null);
      setPostep(0);
      setPickerOtwarty(false);
      if (polRef.current) polRef.current.style.height = 'auto';
      if (nowa && nowa.id) {
        oznaczoneRef.current = Math.max(oznaczoneRef.current, nowa.id);
        setWiadomosci((prev) => scal(prev, [nowa]));
      } else {
        odswiez(false);
      }
      if (onZmianaListy) onZmianaListy();
    } catch (e) {
      if (zywyRef.current) toast.error(e.message || 'Nie udało się wysłać wiadomości');
    } finally {
      if (zywyRef.current) setWysylanie(false);
    }
  };

  const naKlawisz = (e) => {
    // Na waskim ekranie (dotyk) Enter robi nowa linie - wysylka tylko przyciskiem.
    if (e.key === 'Enter' && !e.shiftKey && !waski) {
      e.preventDefault();
      wyslij();
    }
  };

  const naWiadomoscUsunieta = useCallback((id) => {
    setWiadomosci((prev) => prev.map((w) => (
      w.id === id ? { ...w, usunieta: true, tresc: null, zalaczniki: [] } : w
    )));
    if (onZmianaListy) onZmianaListy();
  }, [onZmianaListy]);

  if (!rozmowa) {
    return (
      <div className="kom-okno kom-okno-puste">
        <div className="kom-pusto">
          <div className="kom-pusto-ikona">💬</div>
          <div>Wybierz rozmowę z listy po lewej</div>
        </div>
      </div>
    );
  }

  // --- budowa listy z separatorami dni i grupowaniem naglowkow
  const elementy = [];
  let poprzednia = null;
  wiadomosci.forEach((w) => {
    const nowyDzien = !poprzednia || !tenSamDzien(poprzednia.utworzono, w.utworzono);
    if (nowyDzien) {
      elementy.push(
        <div className="kom-separator-dnia" key={`dzien-${w.id}`}>
          <span>{etykietaDnia(w.utworzono)}</span>
        </div>
      );
    }
    const czasA = parsujDate(poprzednia && poprzednia.utworzono);
    const czasB = parsujDate(w.utworzono);
    const blisko = czasA && czasB && (czasB.getTime() - czasA.getTime()) < PRZERWA_GRUPY;
    const pokazNaglowek = nowyDzien
      || !poprzednia
      || poprzednia.autor_login !== w.autor_login
      || !blisko;
    elementy.push(
      <Wiadomosc
        key={w.id}
        wiadomosc={w}
        pokazNaglowek={pokazNaglowek}
        onPodglad={onPodglad}
        onUsunieta={naWiadomoscUsunieta}
      />
    );
    poprzednia = w;
  });

  const mozeWyslac = (tekst.trim().length > 0 || !!plik) && !wysylanie;

  return (
    <div className="kom-okno">
      <div className="kom-okno-naglowek">
        {waski && (
          <button
            type="button"
            className="kom-ikona-btn"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
            onClick={onWstecz}
            title="Wróć do listy rozmów"
          >
            ←
          </button>
        )}
        <span className="kom-okno-tytul" title={rozmowa.nazwa}>{rozmowa.nazwa}</span>
        {/* Znacznik zasiegu MUSI byc jednoznaczny. Pierwszego dnia na produkcji
            pracownica odpisala na kanale swojego sklepu w przekonaniu, ze pisze
            do wszystkich - kanal sklepu byl oznaczony tylko slowem "sklep",
            co nie mowilo, ze reszta sieci tego NIE zobaczy. */}
        {rozmowa.typ === 'globalny' && (
          <span className="kom-znacznik kom-znacznik-wszyscy">cała sieć</span>
        )}
        {rozmowa.typ === 'sklep' && (
          <span className="kom-znacznik kom-znacznik-sklep" title="Tę rozmowę widzi wyłącznie ten sklep">
            tylko ten sklep
          </span>
        )}
      </div>

      {blad && (
        <div className="kom-pasek-bledu">
          <span>{blad}</span>
          <button
            type="button"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
            onClick={() => odswiez(true)}
          >
            Ponów
          </button>
        </div>
      )}

      <div className="kom-lista-wiadomosci" ref={listaRef} onScroll={naScroll}>
        {maWiecej && (
          <div className="kom-starsze">
            <button
              type="button"
              style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
              onClick={dociagnijStarsze}
              disabled={ladowanieStarszych}
            >
              {ladowanieStarszych ? 'Wczytywanie…' : 'Starsze wiadomości'}
            </button>
          </div>
        )}

        {ladowanie && <div className="kom-info">Wczytywanie wiadomości…</div>}
        {!ladowanie && !wiadomosci.length && !blad && (
          <div className="kom-info">Brak wiadomości. Napisz pierwszą.</div>
        )}

        {elementy}
      </div>

      {nowePonizej && (
        <button
          type="button"
          className="kom-nowe-ponizej"
          style={{ background: 'var(--primary)', border: '1px solid var(--primary)', color: '#fff' }}
          onClick={() => doDolu('smooth')}
        >
          ↓ Nowe wiadomości
        </button>
      )}

      {plik && (
        <div className="kom-podglad-pliku">
          {podgladPliku
            ? <img src={podgladPliku} alt="Podgląd" className="kom-podglad-miniatura" />
            : <span className="kom-plik-ikona">📎</span>}
          <div className="kom-podglad-opis">
            <div className="kom-plik-nazwa">{plik.name}</div>
            <div className="kom-plik-meta">{formatujRozmiar(plik.size)}</div>
            {(plik.type.startsWith('image/') || /\.(heic|heif)$/i.test(plik.name)) && (
              <div className="kom-nota-heic">
                Zdjęcia z iPhone&apos;a (HEIC) przekonwertujemy do JPEG automatycznie.
              </div>
            )}
          </div>
          <button
            type="button"
            className="kom-ikona-btn"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
            onClick={() => setPlik(null)}
            title="Usuń załącznik"
            disabled={wysylanie}
          >
            ×
          </button>
        </div>
      )}

      {wysylanie && postep > 0 && (
        <div className="kom-postep">
          <div className="kom-postep-pasek" style={{ width: `${postep}%` }} />
        </div>
      )}

      <div className="kom-pisanie">
        <div className="kom-pisanie-narzedzia">
          <button
            type="button"
            className="kom-ikona-btn"
            style={{
              background: pickerOtwarty ? 'var(--primary-light)' : 'var(--card-bg)',
              border: `1px solid ${pickerOtwarty ? 'var(--primary)' : 'var(--border)'}`,
            }}
            onClick={() => setPickerOtwarty((v) => !v)}
            title="Emoji"
            aria-label="Emoji"
          >
            😊
          </button>
          <button
            type="button"
            className="kom-ikona-btn"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
            onClick={() => wejscePlikRef.current && wejscePlikRef.current.click()}
            title="Dołącz plik (PDF, DOCX, XLSX, CSV, TXT, ZIP, obraz — do 15 MB)"
            aria-label="Dołącz plik"
            disabled={wysylanie}
          >
            📎
          </button>
          <button
            type="button"
            className="kom-ikona-btn"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
            onClick={() => wejsceZdjecieRef.current && wejsceZdjecieRef.current.click()}
            title="Zdjęcie — HEIC z iPhone'a przekonwertujemy automatycznie"
            aria-label="Zdjęcie"
            disabled={wysylanie}
          >
            📷
          </button>
        </div>

        <textarea
          ref={polRef}
          className="kom-pole"
          rows={1}
          value={tekst}
          /* Podpowiedz nazywa ODBIORCE, a nie samo "napisz wiadomosc" - inaczej
             latwo napisac do wlasnego sklepu w przekonaniu, ze pisze sie do
             calej sieci (zdarzylo sie pierwszego dnia na produkcji). */
          placeholder={
            rozmowa.typ === 'globalny' ? 'Napisz do całej sieci…'
              : rozmowa.typ === 'sklep' ? `Napisz do: ${rozmowa.nazwa} — tylko ten sklep…`
                : `Napisz do: ${rozmowa.nazwa}…`
          }
          title={waski ? 'Napisz wiadomość' : 'Enter wysyła, Shift+Enter nowa linia'}
          onChange={(e) => {
            setTekst(e.target.value);
            const ta = e.target;
            ta.style.height = 'auto';
            ta.style.height = `${Math.min(96, ta.scrollHeight)}px`;
          }}
          onKeyDown={naKlawisz}
          disabled={wysylanie}
        />

        <button
          type="button"
          className="kom-wyslij"
          style={{
            background: mozeWyslac ? 'var(--primary)' : 'var(--card-bg)',
            border: `1px solid ${mozeWyslac ? 'var(--primary)' : 'var(--border)'}`,
            color: mozeWyslac ? '#fff' : 'var(--text-muted)',
          }}
          onClick={wyslij}
          disabled={!mozeWyslac}
          title="Wyślij"
        >
          {wysylanie ? '…' : 'Wyślij'}
        </button>

        {pickerOtwarty && (
          <EmojiPicker
            onWybierz={wstawEmoji}
            onZamknij={() => setPickerOtwarty(false)}
          />
        )}
      </div>

      {/* Ukryte wejscia plikow. accept obrazow zawiera HEIC/HEIF - iOS potrafi
          oddac zdjecie w tym formacie, backend konwertuje je na JPEG. */}
      <input
        ref={wejscePlikRef}
        type="file"
        style={{ display: 'none' }}
        accept=".pdf,.docx,.xlsx,.csv,.txt,.zip,image/*,.heic,.heif"
        onChange={wybierzPlik}
      />
      <input
        ref={wejsceZdjecieRef}
        type="file"
        style={{ display: 'none' }}
        accept="image/*,.heic,.heif"
        capture="environment"
        onChange={wybierzPlik}
      />
    </div>
  );
};

export default OknoRozmowy;
